<script setup>
import { ref, onMounted, onUnmounted, watch, defineEmits, computed } from 'vue';
import { useRouter } from 'vue-router';
import feather from 'feather-icons';
import { useStorage } from '@/composables/use-storage.js';
import { useIcons } from '@/composables/icons.js';
import BadgeInput from '../forms/inputs/BadgeInput.vue';
import {
    openWalletModal,
    walletStatus,
    disconnectWallet
} from '@/plugins/wagmi/connect';
import NotificationsBell from './notification-bell.vue'
import axios from '@/plugins/axios';
import { useExplorerStore } from '@/store/ExplorerStore';
import Modal from '../modals/Modal.vue';
import CreditsTable from '../table/Table.vue';
import CreditsBalance from '../profile/CreditsBalance.vue';
import { useEvals } from '@/composables/use-evals';
import { getApiCreditsBalance } from '@/services/apiCreditsService.js';

const router = useRouter();
const { userAuthToken, userInfo, clearUserData, hasPermission } = useStorage();
const { User, Wallet } = useIcons();
const explorerStore = useExplorerStore();
const { flushEvalBuilderRecoverySessionToCache } = useEvals();

const searchQuery = ref('');
const lastSearchTerm = ref('');
const toggle = ref(false);
const activeIndex = ref('');
const dropdownOpen = ref(false);
const isConnected = ref(walletStatus);
const dropdownToggle = ref(null);
const authToken = ref(userAuthToken.value);
const userInfos = ref(userInfo.value);
const profilePicture = ref(userInfos?.value?.profile_pic);

//Platform Credits
const creditsModalOpen = ref(false);
const creditsBalance = ref(0);
const lastSaleWithCredits = ref([]);
const creditsUsed = ref(0);

// Benchmark credits (prepaid balance for running benchmarks)
const apiCreditsBalance = ref(null);
const apiCreditsLowBalance = ref(false);
const isLoadingApiCredits = ref(false);

const checkScreenSize = () => {
    if (window.innerWidth >= 1024 && toggle.value) {
        toggle.value = false;
        document.body.style.overflow = '';
    }
};

const emit = defineEmits(['reset-filters', 'search']);

const isExplorePage = computed(() => router.currentRoute.value.path === '/explore');

onMounted(() => {
    activeIndex.value = router.currentRoute.value.path;
    
    if (activeIndex.value === '/explore' && router.currentRoute.value.query.search) {
        const queryValue = router.currentRoute.value.query.search;
        searchQuery.value = queryValue;
        lastSearchTerm.value = queryValue;
        explorerStore.setSearchQuery(queryValue);
    } else if (explorerStore.searchQuery) {
        searchQuery.value = explorerStore.searchQuery;
        lastSearchTerm.value = explorerStore.searchQuery;
    }
    
    window.addEventListener('resize', checkScreenSize);
    document.addEventListener('click', handleClickOutside);
    feather.replace();
    scrollToTop();

    const savedTheme = localStorage.getItem('theme') || 'dark';
    document.getElementsByTagName("html")[0].className = savedTheme;

    window.addEventListener('profile-updated', handleProfileUpdate);
    
    if (authToken.value) {
        fetchCredits();
        fetchApiCredits();
    }
});

onUnmounted(() => {
    window.removeEventListener('resize', checkScreenSize);
    document.removeEventListener('click', handleClickOutside);
    window.removeEventListener('profile-updated', handleProfileUpdate);
});

const handler = () => {
    toggle.value = !toggle.value;
    if (toggle.value) {
        document.body.style.overflow = 'hidden';
    } else {
        document.body.style.overflow = '';
    }
};

const handleClickOutside = (event) => {
    if (!dropdownToggle.value.contains(event.target)) {
        dropdownOpen.value = false;
    }
};

const scrollToTop = () => {
    window.scrollTo({ top: 0, behavior: "smooth" });
};

const connect = () => {
    openWalletModal();
};

const logout = async () => {
    try {
        try {
            await flushEvalBuilderRecoverySessionToCache();
        } catch (flushError) {
            console.error("Error flushing eval builder recovery session:", flushError);
        }
        const token = authToken.value;
        const response = await axios.post('/api/logout', {}, {
            headers: {
                'Authorization': `Bearer ${token}`
            }
        });

        if (response.status === 200) {
            clearUserData();
            disconnectWallet();
            router.push('/login');
        }
    } catch (error) {
        console.error("Error logging out:", error);
    }
};

const handleProfileUpdate = (event) => {
    if (event.detail && event.detail.profilePicture) {
        profilePicture.value = event.detail.profilePicture;
    } else {
        profilePicture.value = userInfos?.value?.profile_pic ?? null;
    }

    userInfos.value = userInfo.value;
};

//Platform Credits
const openCreditsModal = () => {
    fetchCredits();
    fetchCreditsHistory();
    creditsModalOpen.value = true;
}

const fetchCredits = async () => {
    try {
        const response = await axios.get('/api/users/credits');
        if (response.data !== null && response.data !== undefined) {
            creditsBalance.value = Number(response.data).toFixed(2);
        } 
    } catch (error) {
        console.error('Error fetching credits:', error);
    }
}

const formatApiCredits = (value) => {
    const num = Number(value ?? 0);
    return num.toLocaleString('en-US', {
        minimumFractionDigits: 0,
        maximumFractionDigits: 2,
    });
};

const fetchApiCredits = async () => {
    if (!authToken.value || isLoadingApiCredits.value) return;
    isLoadingApiCredits.value = true;
    try {
        const data = await getApiCreditsBalance();
        apiCreditsBalance.value = data?.balance_credits ?? 0;
        apiCreditsLowBalance.value = data?.low_balance ?? false;
    } catch (error) {
        console.error('Error fetching Benchmark credits:', error);
        apiCreditsBalance.value = null;
        apiCreditsLowBalance.value = false;
    } finally {
        isLoadingApiCredits.value = false;
    }
};

const openProfileDropdown = () => {
    dropdownOpen.value = !dropdownOpen.value;
    if (dropdownOpen.value && authToken.value) {
        fetchApiCredits();
    }
};

const fetchCreditsHistory = async () => {
    const response = await axios.get('/api/users/credits-history')
    lastSaleWithCredits.value = response.data;
    creditsUsed.value = response.data.reduce((acc, item) => acc + Number(item.amount), 0);
}

const currentPage = ref(1);
const itemsPerPage = ref(5);

const creditsColumns = [
    {
        key: 'listing_name',
        label: 'Listing Name',
        type: 'text',
        redirectable: true
    },
    {
        key: 'amount',
        label: 'Amount',
        type: 'amount',
    },
    {
        key: 'created_at',
        label: 'Created At',
        type: 'date',
    }
]

const sortBy = ref('created_at');
const sortOrder = ref('desc');

const handleSort = (columnKey) => {
    if (sortBy.value === columnKey) {
        sortOrder.value = sortOrder.value === 'asc' ? 'desc' : 'asc';
    } else {
        sortBy.value = columnKey;
        sortOrder.value = 'asc';
    }
    
    fetchCreditsHistory();
};

const handleRedirect = (item, columnKey) => {
    if (columnKey === 'listing_name') {
        
        router.push({ path: `/item-detail/${item.listing_id}` });
    }
}

const isAdmin = computed(() => {
    return userInfo.value?.is_admin === true;
})

const handleSearch = () => {
    searchQuery.value = explorerStore.searchQuery;
    
    if (explorerStore.searchQuery && explorerStore.searchQuery.trim()) {
        explorerStore.forceTabChange('all');
    }
};

const handleEnterSearch = () => {
    const currentValue = explorerStore.searchQuery;
    if (currentValue && currentValue.trim().length >= 2 && router.currentRoute.value.path !== '/explore') {
        lastSearchTerm.value = currentValue.trim();
        router.push({ path: '/explore', query: { search: currentValue.trim() } });
    }
};

const clearSearch = () => {
    searchQuery.value = '';
    lastSearchTerm.value = '';
    explorerStore.setSearchQuery('');
    emit('search', '');

    if (router.currentRoute.value.path === '/explore') {
        const query = { ...router.currentRoute.value.query };
        delete query.search;
        router.replace({ query });
    }
};


watch(() => router.currentRoute.value.query.search, (newSearchQuery) => {
    if (router.currentRoute.value.path === '/explore') {
        const searchValue = newSearchQuery || '';
        if (searchQuery.value !== searchValue) {
            searchQuery.value = searchValue;
            lastSearchTerm.value = searchValue;
            explorerStore.setSearchQuery(searchValue);
        }
    }
}, { immediate: true });

watch(() => explorerStore.searchQuery, (newValue) => {
    if (newValue !== searchQuery.value) {
        searchQuery.value = newValue;
    }
});

watch(() => authToken.value, (newToken) => {
    if (newToken) {
        fetchCredits();
    } else {
        creditsBalance.value = 0;
    }
});
</script>

<template>
    <!-- Credits Modal -->
    <Modal :is-open="creditsModalOpen" @close="creditsModalOpen = false" size="4xl" >
        <template #default>

    <div class="flex flex-col gap-4">
        <CreditsBalance/>

            <CreditsTable
            :show-search="false"
            :show-items-per-page="false"
            :show-pagination="true"
            :data="lastSaleWithCredits"
            :columns="creditsColumns"
            :total-items="lastSaleWithCredits.length"
            v-model:search-query="searchQuery"
            v-model:current-page="currentPage"
            :items-per-page="itemsPerPage"
            :sort-by="sortBy"
            :sort-order="sortOrder"
            @sort="handleSort"
            :redirectable-columns="['listing_name']"
            @redirect="handleRedirect"
            />

        </div>
        </template>
    </Modal>

    <!-- Start Navbar -->
    <nav class="relative overflow-visible z-[9998] h-16 flex items-center w-full backdrop-blur-md bg-black/50 border-b border-echoes-border-gray">
        <!-- Black Navbar Background -->
        <div class="absolute inset-0"></div>
        <!-- Subtle border -->
    
        <div class="relative px-4 z-10 overflow-visible w-full min-w-0" :class="{ '2xl:container 2xl:mx-auto': isExplorePage }">
            <div class="flex items-center justify-between gap-4 relative min-w-0">
                <!-- Left section: Logo -->
                <div class="flex-shrink-0">
                    <router-link to="/explore" class="block">
                        <img src='../../assets/images/avatar/orb_avatar.png' class="block lg:hidden w-12 h-12" alt=""
                            loading="lazy">
                        <div class="hidden lg:block">
                            <img src="../../assets/images/logo-dark.png" class="h-10 w-auto dark:hidden" alt=""
                                loading="lazy">
                            <img src="../../assets/images/logo-dark.png" class="h-6 w-auto hidden dark:block" alt=""
                                loading="lazy">
                        </div>
                    </router-link>
                </div>

                <!-- Center section: Navigation and Search -->
                <div class="hidden lg:flex flex-grow items-center justify-between gap-6 mx-6">
                    <!-- Search input -->
                    <div class="flex-grow max-w-2xl">
                        <BadgeInput v-model="explorerStore.searchQuery" id="navbar-search"
                            placeholder="Search all evals, datasets, domains..."
                            class="w-full navbar-search-input" 
                            @input="handleSearch" 
                            @keyup.enter="handleEnterSearch"
                            @clear-badge="clearSearch">
                            <template #icon>
                                <i class="uil uil-search text-sm"></i>
                            </template>
                            <template #right-icon v-if="explorerStore.searchQuery && !lastSearchTerm">
                                <i class="uil uil-times cursor-pointer text-sm" @click="clearSearch"></i>
                            </template>
                        </BadgeInput>
                    </div>
                    <!-- Navigation menu -->
                    <ul class="flex items-center gap-6">
                        <li>
                            <a href="https://portexai.com/"
                                class="flex items-center text-sm font-medium text-white/80 hover:text-white transition-all duration-200 rounded-lg hover:bg-white/5 px-3 py-2">
                                Home
                            </a>
                        </li>
                        <li>
                            <router-link to="/explore"
                                class="flex items-center text-sm font-medium text-white/80 hover:text-white transition-all duration-200 rounded-lg hover:bg-white/5 px-3 py-2">
                                Datalab
                            </router-link>
                        </li>
                        <li>
                            <a href="https://docs.portexai.com/" target="_blank" rel="noopener noreferrer"
                                class="flex items-center text-sm font-medium text-white/80 hover:text-white transition-all duration-200 rounded-lg hover:bg-white/5 px-3 py-2">
                                Docs
                            </a>
                        </li>
                        <li>
                            <a href="https://research.portexai.com" target="_blank" rel="noopener noreferrer"
                                class="flex items-center text-sm font-medium text-white/80 hover:text-white transition-all duration-200 rounded-lg hover:bg-white/5 px-3 py-2">
                                Research
                            </a>
                        </li>
                    </ul>
                </div>

                <!-- Right section: User Controls -->
                <div class="flex items-center gap-3">
                    <!-- Mobile menu toggle -->
                    <div class="lg:hidden" @click="handler">
                        <button
                            class="p-2 focus:outline-none rounded-lg transition-all duration-300 hover:bg-gradient-to-r hover:from-slate-600/20 hover:to-slate-500/20"
                            :class="toggle ? 'text-slate-200 bg-gradient-to-r from-slate-600/30 to-slate-500/30' : 'text-gray-100'"
                            aria-label="Toggle navigation menu">
                            <i class="uil uil-bars text-2xl"></i>
                        </button>
                    </div>
                    <!-- Wallet connect button -->
                    <div class="hidden sm:inline-block bg-echoes-background-gray hover:bg-echoes-background-gray/80 rounded-md p-1.5" v-if="authToken">
                        <a @click="connect" href="#!" id="connectWallet"
                            :class="isConnected ? 'stroke-emerald-500' : 'stroke-red-500'"
                            class="text-white group relative transition-all duration-300 text-xs cursor-pointer"
                            aria-label="Connect wallet"
                            :title="isConnected ? 'Wallet Connected' : 'Connect your wallet'">
                            <Wallet class="w-5 h-5" />
    
                        </a>
                    </div>

                   

                    <div v-if="authToken" :title="'Notifications'">
                        <NotificationsBell />
                    </div>
                 <!-- User profile dropdown -->
                 <div class="dropdown relative">
                        <button ref="dropdownToggle" @click="openProfileDropdown"
                            class="dropdown-toggle btn btn-icon border-none rounded-full bg-gradient-to-tr from-slate-400 to-slate-600 hover:from-slate-300 hover:to-slate-500 border-slate-500 hover:border-slate-400 transition-all duration-300 group relative shadow-lg hover:shadow-xl hover:shadow-slate-500/30"
                            type="button" :aria-label="authToken ? 'Open user profile menu' : 'Open login menu'"
                            aria-haspopup="true" :aria-expanded="dropdownOpen"
                            data-testid="profile-dropdown-button">
                            <div class="w-10 h-10 overflow-hidden" :title="authToken ? 'My profile' : 'Login'">
                                <img v-if="profilePicture" :src="profilePicture" alt="Profile avatar"
                                    class="rounded-full w-full h-full p-[1px] object-cover shadow-inner" width="40"
                                    height="40">
                                <div v-else class="w-full h-full p-2 text-gray-100">
                                    <User />
                                </div>
                            </div>
                        
                        </button>

                        <!-- Dropdown menu -->
                        <div class="dropdown-menu absolute end-0 m-0 mt-2 z-[9999] w-56 rounded-md overflow-hidden bg-echoes-background-black"
                            v-show="dropdownOpen">
                            <!-- Authenticated user view -->
                            <div v-if="authToken">
                                <div class="relative">
                                    <div class="py-6 bg-gradient-to-tr from-slate-400 to-slate-600"></div>
                                    <div class="absolute px-2 -bottom-8 start-0">
                                        <div class="flex items-center gap-2">
                                            <img v-if="profilePicture" :src="profilePicture" alt=""
                                                class="rounded-full size-12" width="48"
                                                height="48">
                                            <div v-else
                                                class="p-2 w-[3rem] h-[3rem] text-echoes-background-gray rounded-full bg-echoes-background-black">
                                                <User />
                                            </div>
                                            <span class="font-semibold text-[15px] mt-3 truncate max-w-[120px]">@{{ userInfos?.username || ''
                                                }}</span>
                                        </div>
                                    </div>
                                </div>

                                <ul class="py-2 mt-8 text-start divide-y divide-echoes-border-gray">

                                    <!-- Admin Section -->
                                    <li v-if="isAdmin">
                                        <router-link to="/backoffice/listings"
                                            class="flex items-center gap-2 py-2 px-3 rounded-lg bg-echoes-primary hover:bg-echoes-primary/80 text-white/80 hover:text-white transition-all duration-200">
                                            <i class="mdi mdi-account-cog text-[18px] text-white"></i>
                                            <span class="text-sm font-medium">Admin Backoffice</span>
                                        </router-link>
                                    </li>
                                    <!-- Profile Section -->
                                    <li>
                                        <div class="mb-2">
                                            <router-link :to="hasPermission('datasets') ? '/creator-profile?page=eval-builder&subpage=eval-builder' : '/creator-profile?page=datasets&subpage=edit-datasets'"
                                                class="flex items-center gap-2 py-2 px-3 hover:bg-echoes-background-gray/50 text-white/80 hover:text-white transition-all duration-200"
                                                data-testid="data-studio-link">
                                                <div class="flex-shrink-0">
                                                    <i class="uil uil-plus text-[18px] text-echoes-primary"></i>
                                                </div>
                                                <div>
                                                    <p class="text-sm font-medium transition-all duration-200">Data Studio</p>

                                                </div>
                                            </router-link>
                                        </div>
                                        
                                    </li>

                                    <!-- Account Section -->
                                    <li>
                                        <div class="mb-2">
                                            <p
                                                class="px-2 py-1.5 text-xs font-semibold text-white uppercase">
                                                Account</p>
                                            <router-link v-if="hasPermission('buy')" to="/creator-profile?tab=purchases"
                                                class="flex items-center gap-2 py-2 px-3 hover:bg-echoes-background-gray/50 text-white/80 hover:text-white transition-all duration-200">
                                                <i class="uil uil-shopping-cart text-[18px] text-echoes-primary"></i>
                                                <span class="text-sm font-medium">Purchases</span>
                                            </router-link>
                                            <router-link
                                                to="/creator-profile?page=api-credits"
                                                @click="dropdownOpen = false"
                                                class="flex w-full items-center justify-between py-2 px-3 hover:bg-echoes-background-gray/50 text-white/80 hover:text-white transition-all duration-200"
                                                data-testid="api-credits-dropdown-link"
                                            >
                                                <div class="flex items-center gap-2">
                                                    <i class="mdi mdi-lightning-bolt text-[18px] text-echoes-primary"></i>
                                                    <span class="text-sm font-medium">Benchmark credits</span>
                                                    <span
                                                        v-if="apiCreditsLowBalance"
                                                        class="size-1.5 rounded-full bg-amber-400 shrink-0"
                                                        title="Low balance"
                                                    ></span>
                                                </div>
                                                <span
                                                    v-if="isLoadingApiCredits && apiCreditsBalance === null"
                                                    class="text-xs text-white/30 animate-pulse"
                                                >…</span>
                                                <span
                                                    v-else
                                                    class="text-xs font-semibold px-2 py-0.5 rounded-full transition-colors"
                                                    :class="
                                                        apiCreditsLowBalance
                                                            ? 'bg-amber-500/20 text-amber-300'
                                                            : 'bg-echoes-primary/15 text-echoes-primary'
                                                    "
                                                >
                                                    {{ formatApiCredits(apiCreditsBalance) }}
                                                </span>
                                            </router-link>
                                            <button v-if="creditsBalance" @click="openCreditsModal" class="flex w-full items-center justify-between py-2 px-3 hover:bg-echoes-background-gray/50 text-white/80 hover:text-white transition-all duration-200">
                                                <div class="flex items-center gap-2">
                                                    <i class="uil uil-money-bill text-[18px] text-echoes-primary"></i>
                                                    <span class="text-sm font-medium">Marketplace Credits</span>
                                                </div>
                                                <span class="text-sm font-medium text-echoes-primary">({{ creditsBalance }})</span>
                                            </button>
                                            <router-link v-if="hasPermission('listings')"
                                                to="/creator-profile?tab=sales"
                                                class="flex items-center gap-2 py-2 px-3 hover:bg-echoes-background-gray/50 text-white/80 hover:text-white transition-all duration-200">
                                                <i class="uil uil-file-alt text-[18px] text-echoes-primary"></i>
                                                <span class="text-sm font-medium">Sales</span>
                                            </router-link>
                                            <router-link to="/creator-profile-edit"
                                                class="flex items-center gap-2 py-2 px-3 hover:bg-echoes-background-gray/50 text-white/80 hover:text-white transition-all duration-200">
                                                <i class="uil uil-setting text-[18px] text-echoes-primary"></i>
                                                <span class="text-sm font-medium">Settings</span>
                                            </router-link>
                                        </div>
                                    </li>

                                    <!-- Logout Section -->
                                    <li>
                                        <div class="pt-2">
                                            <router-link @click="logout" to="/login"
                                                class="flex items-center gap-2 py-2 px-3 text-red-500 hover:bg-red-600/20 transition-all duration-200"
                                                data-testid="logout-button">
                                                <i class="uil uil-sign-out-alt text-[18px]"></i>
                                                <span class="text-sm font-medium">Logout</span>
                                            </router-link>
                                        </div>
                                    </li>
                                </ul>
                            </div>

                            <!-- Guest user view -->
                            <ul class="py-2 text-start" v-else>
                                <li>
                                    <router-link @click="logout" to="/login"
                                        class="block text-[14px] font-semibold py-2 px-4 text-white/80 hover:text-white transition-all duration-200 hover:bg-echoes-background-gray/50 rounded-lg"
                                        data-testid="login-page-link">
                                        <i class="uil uil-sign-in-alt text-[16px] align-middle me-2"></i>Login
                                    </router-link>
                                </li>
                            </ul>
                        </div>
                    </div>

                </div>
            </div>

            <!-- Mobile Navigation Menu -->
            <div v-show="toggle" class="lg:hidden fixed inset-0 z-[100] overflow-hidden" style="top: 0; height: 100vh;">
                <!-- Mobile Menu Background -->
                <div class="absolute inset-0 bg-black"></div>
                <div class="flex flex-col h-full relative z-10">
                    <!-- Mobile Header -->
                    <div
                        class="flex items-center justify-between border-b border-echoes-border-gray p-4 sticky top-0 bg-black z-10">
                        <router-link to="/" @click="handler" class="flex items-center flex-shrink-0">
                            <img src='../../assets/images/avatar/orb_avatar.png' class="w-12 h-12" alt="" width="48"
                                height="48" loading="lazy">
                        </router-link>

                        <div v-if="router.currentRoute.value.path === '/explore'" class="flex-1 mx-4">
                            <BadgeInput v-model="searchQuery" id="navbar-search-mobile"
                                placeholder="Search all datasets..." 
                                class="w-full" 
                                @input="handleSearch"
                                @clear-badge="clearSearch">
                                <template #icon>
                                    <i class="uil uil-search"></i>
                                </template>
                                <template #right-icon v-if="searchQuery && !lastSearchTerm">
                                    <i class="uil uil-times cursor-pointer" @click="clearSearch"></i>
                                </template>
                            </BadgeInput>
                        </div>

                        <button @click="handler"
                            class="p-2 focus:outline-none text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200 flex-shrink-0">
                            <i class="uil uil-times text-2xl text-gray-100"></i>
                        </button>
                    </div>

                    <!-- Mobile Navigation Links -->
                    <div class="flex-1 overflow-y-auto">
                        <ul class="py-4">
                            <li>
                                <router-link to="/explore" @click="handler"
                                    class="flex items-center px-4 py-3 font-medium hover:bg-gradient-to-r hover:from-slate-600/20 hover:to-slate-500/20 transition-all duration-300 text-gray-100 hover:text-slate-200 rounded-lg mx-2"
                                    :class="activeIndex === '/explore' ? 'text-slate-200 bg-gradient-to-r from-slate-600/30 to-slate-500/30 shadow-md shadow-slate-500/20' : ''">

                                        Datalab
                                </router-link>
                            </li>
                            <li>
                                <a href="https://docs.portexai.com/" target="_blank" rel="noopener noreferrer"
                                    class="flex items-center px-4 py-3 font-medium hover:bg-gradient-to-r hover:from-slate-600/20 hover:to-slate-500/20 transition-all duration-300 text-gray-100 hover:text-slate-200 rounded-lg mx-2">

                                    Docs
                                </a>
                            </li>
                            <li>
                                <a href="https://paragraph.com/@portex" target="_blank" rel="noopener noreferrer"
                                    class="flex items-center px-4 py-3 font-medium hover:bg-gradient-to-r hover:from-slate-600/20 hover:to-slate-500/20 transition-all duration-300 text-gray-100 hover:text-slate-200 rounded-lg mx-2">

                                    Research
                                </a>
                            </li>
                        </ul>

                        <!-- Mobile Wallet Button -->
                        <div class="p-4 mt-auto border-t border-gray-800/50 sticky bottom-0 bg-black hidden" v-if="authToken">
                            <a @click="connect" href="#!"
                                :class="isConnected ? 'bg-gradient-to-tr from-emerald-500 to-emerald-700' : 'bg-gradient-to-tr from-red-500 to-red-700'"
                                class="flex items-center justify-center w-full text-sm py-3 rounded-lg text-white font-medium space-x-2">
                                <Wallet class="w-5 h-5" />
                                <span>{{ isConnected ? 'Wallet Connected' : 'Connect Wallet' }}</span>
                            </a>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    </nav>
    <!-- End Navbar -->
</template>
